import { rem } from 'polished';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { isMSTeams } from 'utils/MSTeams';
import { isEmbeddedTeamsMobileFrame } from 'utils/MSTeams/MSTeamsHelpers';
import { hasMobileApp, small } from 'utils';
import { tracking } from 'App/Tracking';
import { useLocation } from 'react-router';
import { getUtmParametersFromSearch } from 'App/Tracking/trackUtmParamsIfAvailable';
import getIgnoreFromPath, { getLocationInstance } from './getIgnoreFromPath';
import getPlatform from './getPlatform';
import { DownloadMobileAppOverlay } from './DownloadMobileAppOverlay';

export const MobilePreviewImage = styled.img.attrs({
  alt: '',
})`
  width: 298.05px;
  height: 645px;
  margin-bottom: 24px;
  display: none;

  ${small(css`
    display: block;
  `)}
`;

export const Link = styled.a`
  text-decoration: none;
  margin-bottom: ${rem(5)};
  &:not(:first-child) {
    margin-left: ${rem(16)};
  }
`;

export const MOBILE_DOWNLOAD_DISMISSED_KEY = 'store-overlay-dismissed';

export const ConditionalMobileDownloadOverlay = ({
  showOverlayOverride,
  suppressed,
}: {
  showOverlayOverride?: boolean;
  /** Another surface owns the screen; never show, on any viewport. */
  suppressed?: boolean;
}) => {
  let overlayShown = false;
  try {
    overlayShown =
      localStorage.getItem(MOBILE_DOWNLOAD_DISMISSED_KEY) !== null
        ? true
        : false;
  } catch (e) {
    console.error(e);
  }

  const [dismissed, setDismissed] = useState(overlayShown);
  const { search } = useLocation();
  const { utmSource } = getUtmParametersFromSearch(search);

  const persistDismiss = () => {
    try {
      setDismissed(true);
      localStorage.setItem(MOBILE_DOWNLOAD_DISMISSED_KEY, 'true');
    } catch (e) {
      console.error(e);
    }
  };

  const onSkip = () => {
    persistDismiss();
    tracking.track('mobile-web-overlay-clicked', { action: 'dismissed' });
  };

  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);

  const platform = getPlatform(window.navigator);

  const isDesktop = platform === 'desktop';

  const isWithinTeams = isEmbeddedTeamsMobileFrame() || isMSTeams();
  const isIgnoredPath = getIgnoreFromPath(getLocationInstance());
  const isRedirectFromMobile = utmSource === 'mobile_app';

  useEffect(() => {
    if (isRedirectFromMobile) {
      persistDismiss();
    }
  }, [isRedirectFromMobile]);

  const shouldOverride = showOverlayOverride && !dismissed;

  const showOverlay =
    !suppressed &&
    hasMobileApp() &&
    (shouldOverride
      ? isDesktop // only show override if on desktop
      : !isDesktop && !dismissed && !isIgnoredPath && !isWithinTeams);

  const focusDownloadLink = () => {
    if (downloadLinkRef.current) {
      downloadLinkRef.current.focus();
    }
  };

  focusDownloadLink();

  return showOverlay ? <DownloadMobileAppOverlay onSkip={onSkip} /> : null;
};
